import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);
const BG = '#F2F0E9';

// ── Data ────────────────────────────────────────────────────────────────────

type Segment = {
  text: string;
  italic: boolean;
};
const TEXT_SEGMENTS: Segment[] = [{
  text: 'AI shopping is already happening.',
  italic: false
}, {
  text: 'The question is whether your store is part of it.',
  italic: false
}, {
  text: 'AI Storefront',
  italic: true
}, {
  text: 'makes sure it is.',
  italic: false
}];
const COLUMNS = [{
  id: 'discover',
  label: 'DISCOVER',
  labelColor: '#8B5CF6',
  squareColor: '#D6BAFA',
  items: ['AI Shopping Assistant', 'Visual Search', 'Voice Commerce']
}, {
  id: 'personalize',
  label: 'PERSONALIZE',
  labelColor: '#FF5C21',
  squareColor: '#FF5C21',
  items: ['Dynamic Collections', 'Virtual Try-On', 'AI Store Apps']
}, {
  id: 'manage',
  label: 'MANAGE',
  labelColor: '#0EA5E9',
  squareColor: '#82D9F2',
  items: ['Full Customization', 'User Session Inspector', 'Built-in Weekly Insights']
}, {
  id: 'reach',
  label: 'REACH',
  labelColor: '#D97706',
  squareColor: '#FFB739',
  items: ['Shopper Memory', 'Agents-Ready', 'Speaks 70+ Languages']
}];

// ── Tab deep-link map ────────────────────────────────────────────────────────

const TAB_LINKS: Record<string, { section: string; tab: number }> = {
  'AI Shopping Assistant':    { section: 'delight', tab: 0 },
  'AI Store Apps':            { section: 'delight', tab: 1 },
  'Dynamic Collections':      { section: 'delight', tab: 2 },
  'Visual Search':            { section: 'more',    tab: 0 },
  'Shopper Memory':           { section: 'more',    tab: 1 },
  'Virtual Try-On':           { section: 'more',    tab: 2 },
  'Voice Commerce':           { section: 'voice',   tab: 0 },
  'Speaks 70+ Languages':     { section: 'voice',   tab: 0 },
  'Agents-Ready':             { section: 'agents',  tab: 0 },
  'Full Customization':       { section: 'more-2',  tab: 0 },
  'User Session Inspector':   { section: 'more-2',  tab: 1 },
  'Built-in Weekly Insights': { section: 'more-2',  tab: 2 },
};

// ── Feature item with hover arrow ───────────────────────────────────────────

const FeatureItem = ({
  label,
  squareColor,
  onClick,
  isCompact,
}: {
  label: string;
  squareColor: string;
  onClick?: () => void;
  isCompact?: boolean;
}) => {
  const [hovered, setHovered] = useState(false);
  return <div onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined} onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: onClick ? 'pointer' : 'default',
    userSelect: 'none'
  }}>
      {/* 10 px coloured square */}
      <div aria-hidden="true" style={{
      width: '10px',
      height: '10px',
      backgroundColor: squareColor,
      flexShrink: 0
    }} />
      {/* Label */}
      <span style={{
      fontFamily: 'Instrument Sans, sans-serif',
      fontWeight: 500,
      fontSize: isCompact ? '12px' : '14px',
      letterSpacing: '0.04em',
      textTransform: 'uppercase' as const,
      color: '#091717',
      lineHeight: isCompact ? 1.2 : undefined,
      ...(isCompact ? { textBoxTrim: 'both', textBoxEdge: 'cap alphabetic' } as unknown as React.CSSProperties : {})
    }}>
        {label}
      </span>
      {/* Arrow — fades in on hover */}
      <span aria-hidden="true" style={{
      fontFamily: 'Instrument Sans, sans-serif',
      fontWeight: 500,
      fontSize: isCompact ? '12px' : '14px',
      color: '#091717',
      opacity: hovered ? 1 : 0,
      transition: 'opacity 0.18s ease',
      lineHeight: 1
    }}>
        →
      </span>
    </div>;
};

// ── Main component ───────────────────────────────────────────────────────────

export const AIStorefrontFeatures = () => {
  const textRevealRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const [windowWidth, setWindowWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1440);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = windowWidth <= 767;
  const isCompact = windowWidth <= 1024;

  // ── Text-reveal animation (scroll-scrubbed, word by word) ─────────────────
  useEffect(() => {
    const el = textRevealRef.current;
    if (!el) return;
    const ctx = (gsap as any).context(() => {
      const words = el.querySelectorAll('.aisf-word');
      if (!words.length) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        gsap.set(words, { opacity: 1 });
        return;
      }
      gsap.set(words, {
        opacity: 0
      });
      gsap.to(words, {
        opacity: 1,
        ease: 'none',
        stagger: 0.08,
        scrollTrigger: {
          trigger: el,
          start: 'top 80%',
          end: 'center 40%',
          scrub: 1,
          onLeave: (self: any) => {
            self.animation.progress(1);
          },
          onEnterBack: (self: any) => {
            self.animation.progress(1);
          }
        }
      });
    }, el);
    ScrollTrigger.refresh();
    return () => ctx.revert();
  }, []);

  // ── Feature grid: staggered scroll-triggered reveal ───────────────────────
  useEffect(() => {
    const el = featuresRef.current;
    if (!el) return;
    const ctx = (gsap as any).context(() => {
      const header = el.querySelector<HTMLElement>('[data-aisf="header"]');
      const colHdrs = el.querySelectorAll<HTMLElement>('[data-aisf="col-header"]');

      // Collect items per column in DOM order
      const itemGroups = COLUMNS.map((_, ci) => el.querySelectorAll<HTMLElement>(`[data-aisf-col="${ci}"]`));
      const all = [header, ...Array.from(colHdrs), ...itemGroups.flatMap(g => Array.from(g))].filter(Boolean);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        gsap.set(all, { opacity: 1, y: 0 });
        return;
      }
      gsap.set(all, {
        opacity: 0,
        y: 16
      });
      const timer = setTimeout(() => {
        const tl = (gsap as any).timeline({
          scrollTrigger: {
            trigger: el,
            start: 'top 78%',
            once: true
          }
        });

        // 1 — section label
        if (header) tl.to(header, {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: 'power2.out'
        });

        // 2 — all column titles together
        tl.to(colHdrs, {
          opacity: 1,
          y: 0,
          duration: 0.45,
          ease: 'power2.out',
          stagger: 0.04
        }, '-=0.2');

        // 3 — items, column by column
        itemGroups.forEach((items, ci) => {
          tl.to(items, {
            opacity: 1,
            y: 0,
            duration: 0.32,
            ease: 'power2.out',
            stagger: 0.07
          }, ci === 0 ? '+=0.05' : '-=0.18');
        });
        ScrollTrigger.refresh();
      }, 300);
      return () => clearTimeout(timer);
    }, el);
    return () => ctx.revert();
  }, []);
  const fontSize = isMobile ? '40px' : 'clamp(52px, 7vw, 92px)';
  return <>
      {/* ── Section 1: Text reveal ── */}
      <section data-name="AI Storefront Text Reveal" data-nav-theme="light" style={{
      backgroundColor: BG,
      backgroundImage: 'url(https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/intro-section-bg-ukuvealjzb9.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      paddingTop: 'clamp(60px, 8vw, 120px)',
      paddingBottom: 'clamp(60px, 8vw, 120px)'
    }}>
        <div ref={textRevealRef} style={{
        maxWidth: '1360px',
        margin: '0 auto',
        padding: isMobile ? '0 16px' : '0 24px',
        display: 'flex',
        justifyContent: 'flex-start'
      }}>
          <div style={{
          width: '100%',
          maxWidth: isMobile ? '100%' : '1200px',
          marginRight: 'auto',
          textAlign: 'left'
        }}>
            <p style={{
            fontSize,
            lineHeight: 1.15,
            color: '#091717',
            margin: 0,
            letterSpacing: '-0.04em'
          }}>
              {TEXT_SEGMENTS.map((seg, si) => seg.text.split(' ').filter(Boolean).map((word, wi) => <React.Fragment key={`${si}-${wi}`}>
                    <span className={`aisf-word${seg.italic ? ' font-serif italic' : ' font-sans'}`} style={seg.italic ? {
                letterSpacing: '-0.02em'
              } : undefined}>
                      {word}
                    </span>
                    {' '}
                  </React.Fragment>))}
            </p>
          </div>
        </div>
      </section>

      {/* ── Section 2: Feature grid ── */}
      <section data-name="AI Storefront Feature Grid" data-nav-theme="light" style={{
      backgroundColor: BG,
      backgroundImage: 'url(https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/intro-section-bg-ukuvealjzb9.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      paddingBottom: 'clamp(60px, 8vw, 120px)'
    }}>
        <div ref={featuresRef} style={{
        maxWidth: '1360px',
        margin: '0 auto',
        paddingLeft: isMobile ? '16px' : '40px',
        paddingRight: isMobile ? '16px' : '40px'
      }}>
          {/* Label */}
          <div data-aisf="header" style={{
          fontFamily: 'Instrument Sans, sans-serif',
          fontWeight: 400,
          fontSize: isCompact ? '16px' : '20px',
          color: '#091717',
          marginBottom: '40px'
        }}>
            Everything in AI Storefront:
          </div>

          {/* Columns */}
          <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
          gap: isMobile ? '32px 24px' : '0 40px'
        }}>
            {COLUMNS.map((col, ci) => <div key={col.id}>
                {/* Column title */}
                <div data-aisf="col-header" style={{
              fontFamily: 'Instrument Sans, sans-serif',
              fontWeight: 500,
              fontSize: isCompact ? '12px' : '14px',
              letterSpacing: '0.04em',
              textTransform: 'uppercase' as const,
              color: col.labelColor,
              marginBottom: '20px'
            }}>
                  {col.label}
                </div>

                {/* Items */}
                <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '14px'
            }}>
                  {col.items.map((item, ii) => {
                    const link = TAB_LINKS[item];
                    return <div key={item} data-aisf-col={ci}>
                        <FeatureItem label={item} squareColor={col.squareColor} isCompact={isCompact} onClick={link ? () => {
                          window.dispatchEvent(new CustomEvent('aisf-goto', { detail: link }));
                          setTimeout(() => {
                            document.getElementById(`aisf-${link.section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 50);
                        } : undefined} />
                      </div>;
                  })}
                </div>
              </div>)}
          </div>
        </div>
      </section>
    </>;
};